import React from 'react';
import { useNavigate } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Site overview data                                                 */
/* ------------------------------------------------------------------ */
const SITES = [
  {
    name: 'UCLA',
    risk: 'LOW' as const,
    color: '#48bb78',
    sdv: 94,
    queries: 0,
    lastVisit: '2/15/2026',
    patients: ['SYN-001', 'SYN-002', 'SYN-003'],
    warning: '',
  },
  {
    name: 'UCSF',
    risk: 'HIGH' as const,
    color: '#f56565',
    sdv: 71,
    queries: 3,
    lastVisit: '2/1/2026',
    patients: ['SYN-004', 'SYN-005', 'SYN-006'],
    warning: 'On-site audit recommended',
  },
  {
    name: 'Johns Hopkins',
    risk: 'MEDIUM' as const,
    color: '#ecc94b',
    sdv: 86,
    queries: 1,
    lastVisit: '2/20/2026',
    patients: ['SYN-007', 'SYN-008', 'SYN-009'],
    warning: '',
  },
];

/* ------------------------------------------------------------------ */
/*  SDV checklist items                                                */
/* ------------------------------------------------------------------ */
const SDV_ITEMS = [
  {
    level: 'CRITICAL' as const,
    color: '#f56565',
    bg: '#742a2a',
    patient: 'SYN-002 Visit 2',
    issue: 'PK sample sp-004 rejected',
    detail: 'Temperature excursion at \u221260\u00B0C \u2014 required: \u221280\u00B0C per protocol',
    action: 'Review source records',
    button: '\u2192 View Sample',
    route: '/samples',
  },
  {
    level: 'HIGH' as const,
    color: '#ed8936',
    bg: '#744210',
    patient: 'SYN-005 Visit 1',
    issue: 'ctDNA sample sp-009 rejected',
    detail: 'Transit time exceeded limit',
    action: 'Verify chain of custody',
    button: '\u2192 View Sample',
    route: '/samples',
  },
  {
    level: 'MEDIUM' as const,
    color: '#ecc94b',
    bg: '#744210',
    patient: 'SYN-004 Visit 1',
    issue: 'ev-007 PK recording delay 25h',
    detail: 'EDC entry 25 hours after collection',
    action: 'Verify source vs EDC timestamp',
    button: '\u2192 View Evidence',
    route: '/evidence',
  },
];

/* ------------------------------------------------------------------ */
/*  Open queries                                                       */
/* ------------------------------------------------------------------ */
const QUERIES = [
  {
    id: 'Q-001',
    patient: 'SYN-002 Visit 2',
    question: 'PK sample timing \u2014 source shows collection at 6pm but protocol requires 1h post-dose (3pm). Clarify actual time.',
    status: 'Open \u2014 awaiting site response',
    raised: '2/20/2026',
    button: '\u2192 View Query',
    route: '/deviations',
  },
  {
    id: 'Q-002',
    patient: 'SYN-005 Visit 2',
    question: 'Grade 4 febrile neutropenia \u2014 confirm hospitalization date and duration for SAE narrative.',
    status: 'Open \u2014 awaiting PI response',
    raised: '2/22/2026',
    button: '\u2192 View Query',
    route: '/sae',
  },
  {
    id: 'Q-003',
    patient: 'SYN-009 Visit 1',
    question: 'Blood PD sample sp-015 shows hemolysis \u2014 was repeat sample collected?',
    status: 'Open \u2014 awaiting coordinator',
    raised: '3/1/2026',
    button: '\u2192 View Query',
    route: '/samples',
  },
];

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */
export default function HomeMonitor() {
  const navigate = useNavigate();

  return (
    <div style={s.container}>
      {/* ── Header ────────────────────────────────────────────── */}
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Clinical Monitor Dashboard</h1>
          <p style={s.trialId}>DEMO-001 &middot; Sym004 Phase 1b/2a</p>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Last monitor visit:</span>
            <span style={s.metaValue}>3/1/2026</span>
            <span style={{ ...s.metaLabel, marginLeft: 20 }}>Next scheduled:</span>
            <span style={s.metaValue}>4/1/2026</span>
          </div>
        </div>
        <button style={s.roleBtn} onClick={() => navigate('/role-select')}>
          Switch Role
        </button>
      </div>

      {/* ── Site Risk Overview ────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Site Risk Overview</h2>
      <div style={s.siteGrid}>
        {SITES.map((site) => (
          <div key={site.name} style={{ ...s.siteCard, borderTopColor: site.color }}>
            <div style={s.siteHeader}>
              <span style={s.siteName}>{site.name}</span>
              <span style={{ ...s.riskBadge, backgroundColor: site.color + '22', color: site.color, border: `1px solid ${site.color}` }}>
                {site.risk}
              </span>
            </div>
            <div style={s.siteMetrics}>
              <div style={s.metric}>
                <span style={s.metricLabel}>SDV completion</span>
                <span style={{ ...s.metricValue, color: site.sdv >= 90 ? '#48bb78' : site.sdv >= 80 ? '#ecc94b' : '#f56565' }}>
                  {site.sdv}%
                </span>
                <div style={s.progressBar}>
                  <div style={{ ...s.progressFill, width: `${site.sdv}%`, backgroundColor: site.sdv >= 90 ? '#48bb78' : site.sdv >= 80 ? '#ecc94b' : '#f56565' }} />
                </div>
              </div>
              <div style={s.metricRow}>
                <span style={s.metricLabel}>Open queries</span>
                <span style={{ ...s.metricValue, color: site.queries > 0 ? '#ed8936' : '#48bb78' }}>{site.queries}</span>
              </div>
              <div style={s.metricRow}>
                <span style={s.metricLabel}>Last visit</span>
                <span style={s.metricValue}>{site.lastVisit}</span>
              </div>
              <div style={s.metricRow}>
                <span style={s.metricLabel}>Patients</span>
                <span style={{ ...s.metricValue, fontSize: 12 }}>{site.patients.join(', ')}</span>
              </div>
            </div>
            {site.warning && (
              <div style={s.warningBox}>
                {'\u26A0\uFE0F'} {site.warning}
              </div>
            )}
            <button style={{ ...s.cardBtn, color: site.color }} onClick={() => navigate('/safety')}>
              {'\u2192'} Review {site.name}
            </button>
          </div>
        ))}
      </div>

      {/* ── SDV Checklist ─────────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Source Data Verification Queue</h2>
      <p style={s.sectionSub}>Items requiring verification</p>
      <div style={s.sdvList}>
        {SDV_ITEMS.map((item, i) => (
          <div key={i} style={{ ...s.sdvCard, borderLeftColor: item.color }}>
            <div style={s.sdvHeader}>
              <span style={{ ...s.levelBadge, backgroundColor: item.bg, color: item.color }}>{item.level}</span>
              <span style={s.sdvPatient}>{item.patient}</span>
            </div>
            <p style={s.sdvIssue}>{item.issue}</p>
            <p style={s.sdvDetail}>{item.detail}</p>
            <p style={s.sdvAction}>Action: {item.action}</p>
            <button style={{ ...s.cardBtn, color: item.color }} onClick={() => navigate(item.route)}>
              {item.button}
            </button>
          </div>
        ))}
      </div>

      {/* ── Open Queries ──────────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Open Queries &mdash; {QUERIES.length}</h2>
      <div style={s.queryList}>
        {QUERIES.map((q) => (
          <div key={q.id} style={s.queryCard}>
            <div style={s.queryHeader}>
              <span style={s.queryId}>{q.id}</span>
              <span style={s.queryPatient}>{q.patient}</span>
              <span style={s.queryDate}>Raised {q.raised}</span>
            </div>
            <p style={s.queryText}>{q.question}</p>
            <div style={s.queryFooter}>
              <span style={s.queryStatus}>{q.status}</span>
              <button style={{ ...s.cardBtn, color: '#4299e1' }} onClick={() => navigate(q.route)}>
                {q.button}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Generate Report ───────────────────────────────────── */}
      <button style={s.reportBtn} onClick={() => navigate('/decisions')}>
        Generate Monitor Visit Report
      </button>
    </div>
  );
}

/* ================================================================== */
/*  Styles                                                             */
/* ================================================================== */
const s: Record<string, React.CSSProperties> = {
  container: { padding: 32, maxWidth: 960 },

  /* header */
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 },
  trialId: { fontSize: 14, color: '#63b3ed', marginTop: 4 },
  metaRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 },
  metaLabel: { fontSize: 12, color: '#718096' },
  metaValue: { fontSize: 12, color: '#cbd5e0', fontWeight: 600 },
  roleBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #2d3748',
    backgroundColor: '#171923', color: '#a0aec0', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },

  /* section */
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', marginBottom: 8, marginTop: 32, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  sectionSub: { fontSize: 13, color: '#718096', marginBottom: 12, marginTop: 0 },

  /* site cards */
  siteGrid: { display: 'flex', gap: 16, flexWrap: 'wrap' as const },
  siteCard: {
    flex: '1 1 280px', maxWidth: 320, backgroundColor: '#1a202c',
    border: '1px solid #2d3748', borderTop: '3px solid',
    borderRadius: 8, padding: '16px 18px',
  },
  siteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  siteName: { fontSize: 16, fontWeight: 700, color: '#e2e8f0' },
  riskBadge: { fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 12, letterSpacing: 0.5 },
  siteMetrics: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  metric: { display: 'flex', flexDirection: 'column' as const, gap: 4 },
  metricRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  metricLabel: { fontSize: 12, color: '#a0aec0' },
  metricValue: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  progressBar: { height: 4, backgroundColor: '#2d3748', borderRadius: 2, overflow: 'hidden' as const },
  progressFill: { height: '100%', borderRadius: 2, transition: 'width 0.3s' },
  warningBox: {
    marginTop: 10, padding: '6px 10px', borderRadius: 6,
    backgroundColor: '#742a2a', color: '#fc8181', fontSize: 11, fontWeight: 600,
  },
  cardBtn: {
    background: 'none', border: 'none', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', padding: 0, marginTop: 12, transition: 'opacity 0.15s',
  },

  /* SDV list */
  sdvList: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  sdvCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid',
    borderRadius: 8, padding: '14px 18px',
  },
  sdvHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  levelBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, letterSpacing: 0.5 },
  sdvPatient: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  sdvIssue: { fontSize: 13, color: '#cbd5e0', margin: '0 0 4px 0', fontWeight: 600 },
  sdvDetail: { fontSize: 12, color: '#a0aec0', margin: '0 0 4px 0' },
  sdvAction: { fontSize: 12, color: '#ed8936', margin: '0 0 8px 0', fontWeight: 600 },

  /* queries */
  queryList: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  queryCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid #4299e1',
    borderRadius: 8, padding: '14px 18px',
  },
  queryHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  queryId: { fontSize: 12, fontWeight: 700, color: '#63b3ed', fontFamily: 'monospace' },
  queryPatient: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  queryDate: { fontSize: 11, color: '#718096', marginLeft: 'auto' },
  queryText: { fontSize: 13, color: '#cbd5e0', margin: '0 0 10px 0', lineHeight: 1.5 },
  queryFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  queryStatus: { fontSize: 11, color: '#ed8936', fontWeight: 600 },

  /* report button */
  reportBtn: {
    marginTop: 36, width: '100%', padding: '14px 0', borderRadius: 8,
    border: 'none', backgroundColor: '#2b6cb0', color: '#fff',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s',
    letterSpacing: 0.3,
  },
};
